import { useEffect, useRef, useState } from 'react';

export const MIN_REMOTE_SEARCH_LENGTH = 2;
export const REMOTE_SEARCH_DEBOUNCE_MS = 250;

interface UseDebouncedRemoteSearchParams<T> {
  enabled: boolean;
  searchTerm: string;
  /** Primitive identity of everything else the request depends on (org, divisions, types). */
  scopeKey: string;
  search: (normalizedTerm: string) => Promise<T[]>;
}

/**
 * Debounced remote lookup for the sidebar search. Clears results while a new
 * query is pending and drops responses that arrive after the query changed.
 */
const useDebouncedRemoteSearch = <T>({
  enabled,
  searchTerm,
  scopeKey,
  search,
}: UseDebouncedRemoteSearchParams<T>) => {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    const normalizedTerm = searchTerm.trim();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!enabled || normalizedTerm.length < MIN_REMOTE_SEARCH_LENGTH) {
      setResults([]);
      setLoading(false);
      setError(false);
      return undefined;
    }

    setResults([]);
    setLoading(true);
    setError(false);

    const timeout = window.setTimeout(async () => {
      try {
        const nextResults = await searchRef.current(normalizedTerm);
        if (requestId === requestIdRef.current) setResults(nextResults);
      } catch {
        if (requestId === requestIdRef.current) {
          setResults([]);
          setError(true);
        }
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, REMOTE_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      requestIdRef.current += 1;
    };
  }, [enabled, searchTerm, scopeKey]);

  return { results, loading, error };
};

export default useDebouncedRemoteSearch;
